import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { HubPlacePage, hubPlaceQuery } from "@/components/hubs/HubPlacePage";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";

export const Route = createFileRoute("/ski-resorts/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(hubPlaceQuery("ski-resorts", params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Page not found — what Finland" }, { name: "robots", content: "noindex" }] };
    }
    const place = (loaderData as any).place;
    const path = `/ski-resorts/${params.slug}`;
    const url = `https://whatfinland.com${path}`;
    const title = `${place.name} — visitor guide | what Finland`;
    const desc = (place.description ?? "").slice(0, 155) || `Plan your visit to ${place.name} in Finland.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { name: "twitter:card", content: "summary_large_image" },
        ...(place.image_url?.startsWith("https://")
          ? [
              { property: "og:image", content: place.image_url },
              { name: "twitter:image", content: place.image_url },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(path)],
    };
  },
  component: SkiResortsPlaceRoute,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center p-8 text-center">
      <div>
        <h1 className="font-heading text-2xl">Resort not available</h1>
        <Link to="/ski-resorts" className="mt-6 inline-block text-royal underline">
          Browse all ski resorts
        </Link>
      </div>
    </div>
  ),
});

function SkiResortsPlaceRoute() {
  const { slug } = Route.useParams();
  return <HubPlacePage hubSlug="ski-resorts" placeSlug={slug} locale="en" />;
}
